import { useRef } from "react"
import moment from "moment"
import CompanyController from "../controllers/CompanyController"
import SettingController from "../controllers/SettingController"
import FidelityController from "../controllers/FidelityController"
import PopupController from "../controllers/PopupController"
import ComboController from "../controllers/ComboController"
import ProductController from "../controllers/ProductController"
import BeverageController from "../controllers/BeverageController"
import HighlightsController from "../controllers/HighlightsController"
import ScheduleController from "../controllers/ScheduleController"
import CustomerController from "../controllers/CustomerController"
import { getLocalItem, setLocalItem, removeLocalItem, removeSessionItem, getSessionItem } from "../helpers/StorageTools"
import { formatBoolean, getUrlAWSManagerFile } from "../helpers/GeneralTools"
import { checkBetweenIntervalPeriod } from "../helpers/DateTools"
import { getLocalUser } from "../helpers/UserTools"
import { getProductName } from "../helpers/BuilderTools"

const useInitialLoad = ({
  setCompany,
  setSettings,
  setProducts,
  setCombos,
  setBeverages,
  setHighlights,
  setSchedules,
  setFidelities,
  setModalInitialAddress,
  setModalCompanies,
  setOrder,
  setCurrentOrderCombo,
  setLoading,
  setIsContentLoading,
  setUser,
  setUserSelectedAddress,
  setModality,
  setCompanies,
  companyRef,
  userRef,
  userSelectedAddressRef,
  groupFidelitiesRef,
  menuOptionsRef,
  setMenuOptions,
  modality,
  userSelectedAddress,
  modalCompanies,
  removeCookie,
}) => {
  const onSignOut = () => {
    sessionStorage.clear()
    removeLocalItem("user")
    setUserSelectedAddress(null)
    setUser(null)
    setOrder((prev) => ({ ...prev, benefits: [] }))
    if (removeCookie) removeCookie("customerToken")
  }

  const fetchInitialData = async (company, startLoad = false, refreshOrder = false, companies = [], callbacks = {}) => {
    const { onChangeFavicon, onChangeTitlePage, addMenuOption, removeMenuOption, onChangeUserSelectedAddress } = callbacks

    let localUser = getLocalUser()
    let focusMenuOption = true
    setCompany(company)
    companyRef.current = company
    setLocalItem("company", company, true)

    let storageCompanies = getLocalItem("companies", true)

    if (!storageCompanies && companies.length > 0) {
      storageCompanies = companies
      setLocalItem("companies", companies, true)
    }

    setCompanies(storageCompanies)

    let orderItems = getLocalItem("orderItems", true) || []
    if (refreshOrder) {
      setOrder({
        customer: null,
        combos: [],
        products: [],
        beverages: [],
        payments: [],
        benefits: [],
        total: 0.0,
        subTotal: 0.0,
        comments: "",
        discount: "0,00",
        discountType: "R$",
        deliveryFee: 0,
        deliveryComments: "",
      })
      removeSessionItem("order")
    } else {
      const sessionOrder = getSessionItem("order", true)
      if (!!sessionOrder) {
        if (!!sessionOrder.combos && sessionOrder.combos.length > 0) {
          const comboNroList = sessionOrder.combos.map(({ nro_combo }) => nro_combo)
          const maxComboNro = Math.max(...comboNroList)
          setCurrentOrderCombo(maxComboNro)
        }
        setOrder({ ...sessionOrder })
      }
    }

    console.info("CONSULTANDO DADOS DE CONFIGURAÇÕES DO ESTABELECIMENTO...")
    const settingsStorage = getLocalItem("settings", true)

    if (!!settingsStorage) {
      setLoading(false)
      setSettings(settingsStorage)

      if (startLoad) {
        const userAddress = getLocalItem("userSelectedAddress", true)
        if (formatBoolean(settingsStorage.site_get_address_on_early_access)) {
          if (!userAddress) {
            setModalInitialAddress({ status: true, content: { source: "mainOptions" } })
          } else {
            if (formatBoolean(settingsStorage.site_display_company_choose) && companies.length > 1) {
              if (modalCompanies.status === false) {
                setModalCompanies({ status: true, content: storageCompanies })
              }
            }
          }
        } else if (formatBoolean(settingsStorage.site_display_company_choose) && storageCompanies.length > 1) {
          setModalCompanies({ status: true, content: storageCompanies })
        }
      }

      if (!!settingsStorage.site_show_order) {
        orderItems = JSON.parse(settingsStorage.site_show_order)
        setLocalItem("orderItems", orderItems, true)
      }

      onChangeFavicon(getUrlAWSManagerFile(settingsStorage.site_favicon))
      onChangeTitlePage(settingsStorage.site_title || company.nm_company || "Estabelecimento")
    } else {
      setLoading(true)
    }

    // Carregar dados da empresa e configurações (Otimizado)
    const criticalPromise = Promise.all([CompanyController.fetchInitialData()])

    // Separando requisições secundárias (conteúdo)
    const secondaryPromise = Promise.all([
      PopupController.fetchAll(),
      ComboController.fetchAll({ stWebsite: true }),
      ProductController.fetchOptimized(),
      BeverageController.fetchInitial(),
      HighlightsController.fetchAll(),
      ScheduleController.fetchAll(),
    ])

    // Executando críticas primeiro
    await criticalPromise
      .then(([responseInitialData]) => {
        if (responseInitialData.status === 200) {
          const { company: companyData, payments, workingPeriods, workingHours, paymentCategories, settings } = responseInitialData.data

          // Process Settings
          if (startLoad) {
            const companies = getLocalItem("companies", true)
            const userAddress = getLocalItem("userSelectedAddress", true)

            if (formatBoolean(settings.site_get_address_on_early_access)) {
              if (!userAddress) {
                setModalInitialAddress({ status: true, content: { source: "mainOptions" } })
              } else {
                if (formatBoolean(settings.site_display_company_choose) && companies.length > 1) {
                  if (modalCompanies.status === false) {
                    setModalCompanies({ status: true, content: companies })
                  }
                }
              }
            } else if (formatBoolean(settings.site_display_company_choose) && companies.length > 1) {
              if (modalCompanies.status === false || userAddress === null) {
                setModalCompanies({ status: true, content: companies })
              }
            }
          }

          onChangeFavicon(getUrlAWSManagerFile(settings.site_favicon))
          onChangeTitlePage(settings.site_title || company.nm_company || "Restaurante")

          if (!!settings.site_show_order) {
            orderItems = JSON.parse(settings.site_show_order)
            setLocalItem("orderItems", orderItems, true)
          }
          setSettings(settings)
          setLocalItem("settings", settings, true)

          // Process Company Data
          const companyLocalData = storageCompanies.find((c) => c.id_company === companyData.id_company)
          company = { ...company, ...companyData, company_address: companyLocalData?.company_address, payment_status: "idle" }

          if (!companyRef.current?.company_modality?.find(({ fk_id_modality }) => fk_id_modality === 1)) {
            setUserSelectedAddress(null)
            setLocalItem("userSelectedAddress", null, true)
            userSelectedAddressRef.current = null
            setModality(2)
          }

          let enabledPayments = payments
            .filter((method) => method.st_website)
            .map((method) => ({
              ds_key_payment_method: method.ds_key_payment_method,
              ds_key_type_payment_method: method.ds_key_type_payment_method,
              ds_obs_payment_method: method.ds_obs_payment_method,
              ...method.payment_method,
            }))
          company = { ...company, company_payment_methods: enabledPayments }

          company = { ...company, company_working_periods: workingPeriods }
          company = { ...company, company_working_hours: workingHours }
          company = { ...company, company_payment_categories: paymentCategories }

          setCompany(company)
          companyRef.current = company
          setLocalItem("company", company, true)
        }
      })
      .catch((error) => {
        console.error("Erro ao carregar dados iniciais:", error)
      })
      .finally(() => {
        setLoading(false)
      })

    // Carregar dados do Storage (Cache) para exibição imediata
    const combosSession = getLocalItem("combos", true)
    if (!!combosSession) {
      addMenuOption({
        companyId: company.id_company,
        id: "combos",
        label: "Combos",
        focus: focusMenuOption,
        position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "combos")?.nro_priority : 1,
        visible: combosSession.length > 0,
      })
      focusMenuOption = false
      setCombos(combosSession)
    }

    const productsSession = getLocalItem("products", true)
    if (!!productsSession) {
      for (const product of productsSession) {
        if (product?.flavours?.length > 0 || product?.sizes?.length > 0) {
          addMenuOption({
            companyId: company.id_company,
            id: product.uuid_product,
            label: getProductName(product),
            focus: focusMenuOption,
            position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "produtos")?.nro_priority : 2,
            visible: true,
          })
          focusMenuOption = false
        }
      }
      setProducts(productsSession)
    }

    const beveragesSession = getLocalItem("beverages", true)
    if (!!beveragesSession) {
      addMenuOption({
        companyId: company.id_company,
        id: "beverages",
        label: "Bebidas",
        focus: focusMenuOption,
        position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "bebidas")?.nro_priority : 3,
        visible: beveragesSession.length > 0,
      })
      focusMenuOption = false
      setBeverages([...beveragesSession])
    }

    const highlightsSession = getLocalItem("highlights", true)
    if (!!highlightsSession) {
      addMenuOption({
        companyId: company.id_company,
        id: "highlights",
        label: "Destaques",
        focus: focusMenuOption,
        position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "destaques")?.nro_priority : 0,
        visible: highlightsSession.length > 0,
      })
      focusMenuOption = false
      setHighlights(highlightsSession)
    }

    const schedulesSession = getLocalItem("schedules", true)
    if (!!schedulesSession) {
      setSchedules([...schedulesSession])
    }

    // Executando secundárias em background
    secondaryPromise
      .then(async ([responsePopups, responseCombos, responseProducts, responseBeverages, responseHighlights, responseSchedule]) => {
        // Fidelities logic moved to background execution

        // User Data
        if (!!localUser) {
          setUser(localUser)
          userRef.current = localUser

          let user = localUser
          //Caso já tenha usuário logado, busca novamente os dados dele para atualizar quaisquer informações que possam
          //ter sido alteradas como pontos de fideldiade e endereços e no futuro cupons
          CustomerController.fetchMe({ withOrders: true, withStreets: true, withMovements: true, withFidelities: true, withLastOrder: true })
            .then(async (responseCustomer) => {
              if (responseCustomer.status === 200) {
                if (responseCustomer?.data?.dt_blocked_at) {
                  console.error("Usuário não autorizado, limpando dados do usuário")
                  setUser(null)
                  userRef.current = null
                  setUserSelectedAddress(null)
                  setLocalItem("userSelectedAddress", null, true)
                  userSelectedAddressRef.current = null
                  setLocalItem("user", null, true)

                  sessionStorage.clear()
                  removeLocalItem("user")
                  setOrder({
                    customer: null,
                    combos: [],
                    products: [],
                    beverages: [],
                    payments: [],
                    benefits: [],
                    total: 0.0,
                    subTotal: 0.0,
                    comments: "",
                    discount: "0,00",
                    discountType: "R$",
                    deliveryFee: 0,
                    deliveryComments: "",
                  })
                  removeSessionItem("order")
                } else {
                  const addresses = responseCustomer.data?.streets || []
                  const customer_orders = responseCustomer.data?.customer_orders
                  const fidelities = responseCustomer.data?.customer_fidelities_points || []
                  const movements_account = responseCustomer.data?.movements_account || []
                  const last_active_order = responseCustomer.data?.last_active_order || null

                  const cashbackUsed = getLocalItem("cashbackUsed")
                  if (movements_account.length > 0 && cashbackUsed) {
                    const [dateUsed, val_used] = cashbackUsed.split("__")
                    for (const movement of movements_account) {
                      const { type_movement_customer_account, dt_deleted_at } = movement
                      if (!dt_deleted_at && type_movement_customer_account === "output") {
                        const { dt_created_at } = movement
                        const sameDay = moment(dateUsed).isSame(dt_created_at, "D")
                        const sameMonth = moment(dateUsed).isSame(dt_created_at, "M")
                        const sameYear = moment(dateUsed).isSame(dt_created_at, "Y")
                        if (sameDay && sameMonth && sameYear) {
                          const { val_movement } = movement
                          if (parseFloat(Number.parseFloat(val_used).toFixed(2)) === val_movement) {
                            removeLocalItem("cashbackUsed")
                          }
                        }
                      }
                    }
                  }

                  if (addresses.length > 0) {
                    const defaultAddress =
                      addresses.find(({ customer_address: { st_primary, dt_deleted_at } }) => st_primary && !dt_deleted_at) || addresses[0]

                    if (
                      !userSelectedAddress?.nm_street &&
                      modality !== 2 &&
                      modality !== 3 &&
                      !!companyRef.current?.company_modality?.find(({ fk_id_modality }) => fk_id_modality === 1)
                    ) {
                      setModality(1)
                      setLocalItem("modality", 1)
                      onChangeUserSelectedAddress(defaultAddress, false, null, true).then()
                    }
                  }

                  setUser({ ...user, customer_orders, addresses, fidelities, movements_account, last_active_order })
                  userRef.current = { ...user, customer_orders, addresses, fidelities, movements_account, last_active_order }
                  setLocalItem("user", { ...user, customer_orders, addresses, fidelities, movements_account, last_active_order }, true)
                }
              }

              if (responseCustomer.status === 403) {
                onSignOut()
              }
            })
            .catch((error) => {
              console.error("Falha ao atualizar dados do cliente", error)
              setUser(user)
              userRef.current = user
            })
        }

        // Popups
        if (responsePopups.status === 200) {
          setLocalItem("popups", responsePopups, true)
        }

        // Combos
        const currentDate = moment()
        if (responseCombos.status === 200) {
          const combosData = responseCombos.data.rows || responseCombos.data || []
          if (combosData.length > 0) {
            const combosFiltered = combosData
              .filter(({ combo_periods = [] }) => checkBetweenIntervalPeriod(currentDate, combo_periods, "combos_periods_times"))
              .sort((a, b) => a.nro_priority - b.nro_priority)

            if (!combosSession) {
              addMenuOption({
                companyId: company.id_company,
                id: "combos",
                label: "Combos",
                focus: focusMenuOption,
                position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "combos")?.nro_priority : 1,
                visible: combosFiltered.length > 0,
              })
              focusMenuOption = false
            }
            setCombos(combosFiltered)
            setLocalItem("combos", combosFiltered, true)
          }
        }

        // PRODUTOS - OTIMIZAÇÃO
        if (responseProducts.status === 200) {
          // O endpoint otimizado já retorna sizes e flavours
          // Precisamos apenas filtrar por horário/período
          const validProducts = responseProducts.data.rows
            .filter(({ products_periods, st_period_control }) =>
              st_period_control
                ? !!products_periods && products_periods.length === 7
                  ? !!checkBetweenIntervalPeriod(currentDate, products_periods, "products_periods_times")
                  : true
                : true
            )
            .sort((a, b) => a.nro_priority - b.nro_priority)

          setProducts(validProducts)
          setLocalItem("products", validProducts, true)

          // Update Menu Options
          validProducts.forEach((product) => {
            // Check if menu option already exists to avoid duplicates or unnecessary updates
            const existingOption = menuOptionsRef.current.find((opt) => opt.id === product.uuid_product)
            if (!existingOption) {
              addMenuOption({
                companyId: company.id_company,
                id: product.uuid_product,
                label: getProductName(product),
                focus: focusMenuOption,
                position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "produtos")?.nro_priority : 2,
                visible: true,
              })
            }
          })
        }

        // Beverages
        if (responseBeverages.status === 200 && responseBeverages.data.length > 0) {
          const beverages = responseBeverages.data
          addMenuOption({
            companyId: company.id_company,
            id: "beverages",
            label: "Bebidas",
            focus: focusMenuOption,
            position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "bebidas")?.nro_priority : 3,
            visible: beverages.length > 0,
          })
          focusMenuOption = false

          // Verificando se existe a opção sem bebida
          const beveragesFiltered = beverages
            .filter(
              ({ nm_beverage, beverages_periods }) =>
                `${nm_beverage}`.toLowerCase().indexOf("sem b") === -1 &&
                `${nm_beverage}`.toLowerCase().indexOf("sem r") === -1 &&
                (!!beverages_periods && beverages_periods.length === 7
                  ? !!checkBetweenIntervalPeriod(currentDate, beverages_periods, "beverages_periods_times")
                  : true)
            )
            .sort((a, b) => a.nro_priority - b.nro_priority)
          setBeverages(beveragesFiltered)
          setLocalItem("beverages", beveragesFiltered, true)
        }

        // Highlights
        if (responseHighlights.status === 200) {
          const fetchedHighlights = responseHighlights.data.rows.filter(({ st_website }) => st_website)
          setHighlights(fetchedHighlights)
          setLocalItem("highlights", fetchedHighlights, true)

          if (fetchedHighlights.length > 0 && !highlightsSession) {
            addMenuOption({
              companyId: company.id_company,
              id: "highlights",
              label: "Destaques",
              focus: focusMenuOption,
              position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "destaques")?.nro_priority : 0,
              visible: true,
            })
          }
        }

        // Schedules
        if (responseSchedule.status === 200) {
          setSchedules(responseSchedule.data)
          setLocalItem("schedules", responseSchedule.data, true)
        }
      })
      .catch((error) => {
        console.error(error)
        setIsContentLoading(false)
      })
      .finally(() => {
        setIsContentLoading(false)
      })

    // Carregamento de fidelidades em background (não bloqueante)
    FidelityController.fetchAll().then((responseFidelities) => {
      if (responseFidelities.status === 200) {
        setFidelities(responseFidelities.data)
        for (let fidelity of responseFidelities.data) {
          if (fidelity.nm_fidelity_group) {
            if (!groupFidelitiesRef.current[fidelity.nm_fidelity_group]) {
              groupFidelitiesRef.current[fidelity.nm_fidelity_group] = [fidelity.id_fidelity]
            } else {
              groupFidelitiesRef.current[fidelity.nm_fidelity_group].push(fidelity.id_fidelity)
            }
          }
        }

        //Validando se existe
        let fidelityUsageControl = getLocalItem("fidelityUsageControl", true) || {}
        let newFidelityUsageControl = {}
        for (let index = 0; index < Object.keys(fidelityUsageControl).length; index++) {
          const fidelityId = Object.keys(fidelityUsageControl)[index]
          const fidelityUsage = Object.values(fidelityUsageControl)[index]
          const newFidelityUsage = []
          for (const item of fidelityUsage) {
            const currentTime = Date.now()
            const usageTime = typeof item.usedAt === "number" ? item.usedAt : new Date(item.usedAt).getTime()
            const hoursPassed = (currentTime - usageTime) / (1000 * 60 * 60)
            if (hoursPassed < 24) {
              newFidelityUsage.push(item)
            }
          }
          if (newFidelityUsage.length > 0) {
            newFidelityUsageControl[fidelityId] = newFidelityUsage
          }
        }
        setLocalItem("fidelityUsageControl", newFidelityUsageControl, true)
      }
    })
  }

  return { fetchInitialData }
}

export default useInitialLoad
